import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// components
import LoadingScreen from '../components/LoadingScreen';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { withPersonalData } from '../components/withPersonalData';
import WelcomePage from 'pages/WelcomePage';
import LoginPage from 'pages/LoginPage';
// import PersonalData from 'pages/PersonalData';
import SkillLevelPage from '../pages/SkillLevelPage';
import StartVideoInterviewPage from '../pages/StartVideoInterviewPage';
import VideoInterviewRecordPage from 'pages/VideoInterviewRecordPage';
import { InterviewProvider } from '../contexts/InterviewContext';
import { PATHS } from './paths';
import CheckDevicesPage from 'pages/CheckDevices';
import Profile from 'pages/Profile';
import MainPage from 'pages/MainPage';
import InterviewsListPage from 'pages/InterviewsListPage';
import SettingsPage from 'pages/SettingsPage';
import InterviewResultPage from 'pages/InterviewResultPage';
import PaymentPage from 'pages/PaymentPage';
import PaymentSuccessPage from 'pages/PaymentSuccessPage';
import PaymentFailPage from 'pages/PaymentFailPage';
import NewCompanyPage from 'pages/NewCompanyPage';
import NewVacancyPage from 'pages/NewVacancyPage';
import MyCompanyPage from 'pages/MyCompanyPage';
import VacancyPage from 'pages/VacancyPage';
import VacancyStatsPage from 'pages/VacancyStatsPage';
import VacancyInfoPage from 'pages/RequestedIntreview/VacancyInfoPage';
import ExtractSkills from 'pages/ExtractSkills';
import PaymentHrInvitationsPage from 'pages/PaymentHr';
import CandidateResultPage from 'pages/CandidateResultPage';
import Chat from 'pages/Chat';
import AuthSuccessPage from 'pages/AuthSuccessPage';
import InvitePage from 'pages/InvitePage';
import SharedResultsPage from 'pages/SharedResultsPage';
// ----------------------------------------------------------------------

const Loadable = (Component: any) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    // Main Routes
    {
      path: PATHS.videoInterview.candidate,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: (
            <ProtectedRoute>
              {withPersonalData(StartVideoInterviewPage)({ type: 'candidate' })}
            </ProtectedRoute>
          )
        }
      ]
    },
    {
      path: PATHS.hr.newCompany,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(NewCompanyPage)({})}</ProtectedRoute>
        }
      ]
    },
    {
      path: PATHS.hr.newVacancy,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(NewVacancyPage)({})}</ProtectedRoute>
        }
      ]
    },
    {
      path: PATHS.hr.company,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(MyCompanyPage)({})}</ProtectedRoute>
        }
      ]
    },
    {
      path: PATHS.hr.vacancy,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(VacancyPage)({})}</ProtectedRoute>
        }
      ]
    },
    {
      path: PATHS.hr.vacancyStats,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(VacancyStatsPage)({})}</ProtectedRoute>
        }
      ]
    },
    {
      path: '/video-interview/record/:interviewId',
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: (
            <ProtectedRoute>
              <InterviewProvider>
                {withPersonalData(VideoInterviewRecordPage)({})}
              </InterviewProvider>
            </ProtectedRoute>
          )
        }
      ]
    },
    {
      path: '/requested-interviews/video-interview/record/:interviewId',
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: (
            <ProtectedRoute>
              <InterviewProvider>
                {withPersonalData(VideoInterviewRecordPage)({})}
              </InterviewProvider>
            </ProtectedRoute>
          )
        }
      ]
    },
    {
      path: PATHS.hr.candidateResult,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: (
            <ProtectedRoute>
              <InterviewProvider>
                {withPersonalData(CandidateResultPage)({})}
              </InterviewProvider>
            </ProtectedRoute>
          )
        }
      ]
    },
    {
      path: PATHS.videoInterview.checkDevices,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: (
            <ProtectedRoute>
              {withPersonalData(CheckDevicesPage)({ type: 'candidate' })}
            </ProtectedRoute>
          )
        }
      ]
    },
    {
      path: `${PATHS.extractSkills}/:id`,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(ExtractSkills)({})}</ProtectedRoute>
        }
      ]
    },
    {
      path: `${PATHS.skillLevel}/:id`,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(SkillLevelPage)({})}</ProtectedRoute>
        }
      ]
    },
    {
      path: PATHS.profile,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(Profile)({})}</ProtectedRoute>
        }
      ]
    },
    // Личные сообщения
    {
      path: PATHS.chat,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(Chat)({})}</ProtectedRoute>
        }
      ]
    },
    // Главная страница
    {
      path: PATHS.main,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(MainPage)({})}</ProtectedRoute>
        }
      ]
    },
    {
      path: PATHS.settings,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(SettingsPage)({})}</ProtectedRoute>
        }
      ]
    },
    // Welcome страница
    {
      path: '/',
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(MainPage)({})}</ProtectedRoute>
        }
      ]
    },
    // Логин
    {
      path: '/login',
      
      children: [{ index: true, element: <LoginPage /> }]
    },
    // OAuth (Яндекс) — приёмник токенов после серверного редиректа
    {
      path: '/auth/success',
      element: <LogoOnlyLayout />,
      children: [{ index: true, element: <AuthSuccessPage /> }]
    },
    // Принятие приглашения по ссылке из письма: /invite?token=...
    {
      path: PATHS.invite,
      element: <LogoOnlyLayout />,
      children: [{ index: true, element: <InvitePage /> }]
    },
    {
      path: PATHS.paymentSuccess,
      element: <LogoOnlyLayout />,
      children: [{ index: true, element: <PaymentSuccessPage /> }]
    },
    {
      path: PATHS.paymentFail,
      element: <LogoOnlyLayout />,
      children: [{ index: true, element: <PaymentFailPage /> }]
    },
    {
      path: PATHS.payment,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(PaymentPage)({})}</ProtectedRoute>
        }
      ]
    },
    {
      path: PATHS.paymentHrInvitations,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(PaymentHrInvitationsPage)({})}</ProtectedRoute>
        }
      ]
    },
    {
      path: PATHS.interviews,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(InterviewsListPage)({})}</ProtectedRoute>
        }
      ]
    },
    {
      path: PATHS.results,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(InterviewResultPage)({})}</ProtectedRoute>
        }
      ]
    },
    // Публичные результаты по расшаренной ссылке — без ProtectedRoute
    {
      path: PATHS.sharedResults,
      children: [{ index: true, element: <SharedResultsPage /> }]
    },
    {
      path: PATHS.requestedInterviews.vacancyInfo,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(VacancyInfoPage)({})}</ProtectedRoute>
        }
      ]
    },
    {
      path: PATHS.requestedInterviews.checkDevices,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(CheckDevicesPage)({})}</ProtectedRoute>
        }
      ]
    },
    {
      path: PATHS.settings,
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(SettingsPage)({})}</ProtectedRoute>
        }
      ]
    },
    // Catch all route - должен быть последним
    {
      path: '/video-interview/:id/result',
      element: <LogoOnlyLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute>{withPersonalData(InterviewResultPage)({})}</ProtectedRoute>
        }
      ]
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    }
  ]);
}

// IMPORT COMPONENTS
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
